import { ActivePeriodStateType } from './common/customerConditions'
import { CopiedDiscountInfo, DiscountInfo } from './common/discounts'
import { FlowErrorType } from './common/error'
import {
  BannerConfigType,
  CouponVisibilityConfigType,
  DeliveryInfoConfigType,
  OrderSummaryConfigType,
  ThemeColorPaletteType,
  TrustBadgesConfigType
} from './customizeUI'
import { MetricToWidgetId } from './experimentAnalytics'

export enum ExperimentSteps {
  TEST_GROUPS = 'test_groups',
  METRICS = 'metrics',
  PREVIEW = 'preview'
}

export enum ExperimentStatusOptions {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  PAUSED = 'PAUSED'
}

export enum ExperimentMetricOptions {
  CONVERSION_PERCENTAGE = 'CONVERSION_PERCENTAGE',
  CONVERSION_WITH_DISCOUNT = 'CONVERSION_WITH_DISCOUNT',
  CHECKOUT_INITIATED_TO_LOGIN_COMPLETED = 'CHECKOUT_INITIATED_TO_LOGIN_COMPLETED',
  LOGIN_COMPLETED_TO_ADDRESS_SUBMITTED = 'LOGIN_COMPLETED_TO_ADDRESS_SUBMITTED',
  ADDRESS_SUBMITTED_TO_PAYMENT_METHOD_SELECTED = 'ADDRESS_SUBMITTED_TO_PAYMENT_METHOD_SELECTED',
  PAYMENT_METHOD_SELECTED_TO_ORDER_COMPLETED = 'PAYMENT_METHOD_SELECTED_TO_ORDER_COMPLETED',
  SALES_PERCENTAGE = 'SALES_PERCENTAGE',
  NUMBER_OF_ORDERS = 'NUMBER_OF_ORDERS',
  DISCOUNT_BURN = 'DISCOUNT_BURN',
  PREPAID_SHARE = 'PREPAID_SHARE',
  UPI_SHARE = 'UPI_SHARE',
  COD_SHARE = 'COD_SHARE',
  AOV = 'AOV'
}

export enum ExperimentCreationOptions {
  COUPONS = 'coupons',
  PAYMENT_OFFERS = 'payment-offers',
  UI = 'ui',
  EXIT_COUPONS = 'exit-coupons'
}

export type ExperimentCreationType =
  | ExperimentCreationOptions.COUPONS
  | ExperimentCreationOptions.PAYMENT_OFFERS
  | ExperimentCreationOptions.UI
  | ExperimentCreationOptions.EXIT_COUPONS

export type ExperimentCreatedDiscountType = {
  created: DiscountInfo
  copied?: CopiedDiscountInfo
}

export type ExperimentUIConfigType = {
  bannerConfigs: {
    loginBanner: BannerConfigType
    addressBanner: BannerConfigType
    paymentBanner: BannerConfigType
  }
  buttonCTATexts: {
    loginButtonCTAText: string
    addressButtonCTAText: string
  }
  couponVisibilityConfig: {
    loginCouponVisibility: CouponVisibilityConfigType
    addressCouponVisibility: CouponVisibilityConfigType
    paymentsCouponVisibility: CouponVisibilityConfigType
  }
  trustBadgesConfig: TrustBadgesConfigType
  orderSummaryConfig: OrderSummaryConfigType
  brandLogoURL: string
  colorPalette: ThemeColorPaletteType
}

export type TestVariant = {
  id: string
  name: string
  description: string
  trafficPercentage: number
  isControlGroup: boolean
  isIncluded: boolean
  start?: number
  end?: number
  entityIdMapping?: any
  defaultSegmentId?: string
}

export type DiscountTestVariantType = TestVariant & {
  selectedDiscounts: ExperimentCreatedDiscountType[]
}

export type PrepaidOfferTestVariantType = TestVariant & {
  selectedPrepaidOffers: ExperimentCreatedDiscountType[]
}

export type UIExperimentTestVariantType = TestVariant & {
  config: ExperimentUIConfigType
}

export type ExperimentTestVariantType =
  | {
      type: ExperimentCreationOptions.COUPONS
      value: DiscountTestVariantType
    }
  | {
      type: ExperimentCreationOptions.PAYMENT_OFFERS
      value: PrepaidOfferTestVariantType
    }
  | {
      type: ExperimentCreationOptions.UI
      value: UIExperimentTestVariantType
    }

export type ExperimentMetricType = {
  type: ExperimentMetricOptions
  isSelected: boolean
  isPrimary: boolean
}

export type ExperimentStateType = {
  type: ExperimentCreationType
  id: string
  name: string
  description: string
  currentStep: ExperimentSteps
  variants: ExperimentTestVariantType[]
  metrics: ExperimentMetricType[]
  activePeriod: ActivePeriodStateType
  status: ExperimentStatusOptions
  previewLink?: string
  errorState: ExperimentErrorStateType
}

export type ExperimentErrorStateType = {
  name: FlowErrorType
  description: FlowErrorType
  variants: {
    title: FlowErrorType
    selection: FlowErrorType
  }[]
  testGroups: FlowErrorType
  trafficAllocation: FlowErrorType
  metrics: FlowErrorType
  activePeriod: {
    startDate: FlowErrorType
    endDate: FlowErrorType
  }
}

export enum ExperimentActionSet {
  SET_STATE,
  SET_TYPE,
  SET_NAME,
  SET_DESCRIPTION,
  SET_CURRENT_STEP,
  SET_ERROR_STATE,
  SET_EXPERIMENT,
  ADD_VARIANT,
  REMOVE_VARIANT,
  UPDATE_VARIANT,
  UPDATE_METRIC,
  SET_ACTIVE_PERIOD
}

export type ExperimentActionType =
  | {
      type: ExperimentActionSet.SET_STATE
      payload: ExperimentStateType
    }
  | {
      type: ExperimentActionSet.SET_TYPE
      payload: ExperimentCreationType
    }
  | {
      type: ExperimentActionSet.SET_NAME
      payload: string
    }
  | {
      type: ExperimentActionSet.SET_DESCRIPTION
      payload: string
    }
  | {
      type: ExperimentActionSet.SET_CURRENT_STEP
      payload: ExperimentSteps
    }
  | {
      type: ExperimentActionSet.SET_ERROR_STATE
      payload: ExperimentErrorStateType
    }
  | {
      type: ExperimentActionSet.SET_EXPERIMENT
      payload: ExperimentStateType
    }
  | {
      type: ExperimentActionSet.ADD_VARIANT
    }
  | {
      type: ExperimentActionSet.REMOVE_VARIANT
      payload: number
    }
  | {
      type: ExperimentActionSet.UPDATE_VARIANT
      payload: { index: number; variant: ExperimentTestVariantType }
    }
  | {
      type: ExperimentActionSet.UPDATE_METRIC
      payload: { index: number; metric: ExperimentMetricType }
    }
  | {
      type: ExperimentActionSet.SET_ACTIVE_PERIOD
      payload: ActivePeriodStateType
    }

export interface ExperimentContextType {
  state: ExperimentStateType
  actions: {
    setState: (state: ExperimentStateType) => void
    setType: (type: ExperimentCreationType) => void
    setName: (name: string) => void
    setDescription: (description: string) => void
    setCurrentStep: (step: string) => void
    setErrorState: (errorState: ExperimentErrorStateType) => void
    setExperiment: (experiment: ExperimentStateType) => void
    addVariant: () => void
    removeVariant: (index: number) => void
    updateVariant: (index: number, variant: ExperimentTestVariantType) => void
    updateMetric: (index: number, metric: ExperimentMetricType) => void
    setActivePeriod: (activePeriod: ActivePeriodStateType) => void
  }
}

export type ExperimentListItemType = {
  id: string
  name: string
  description: string
  status: ExperimentStatusOptions
  createdAt: Date
  updatedAt: Date
  startTime: Date
  endTime: Date | null
  type: ExperimentCreationType
}

export type ExperimentAnalyticsItemType = ExperimentListItemType & {
  primaryMetric: { label: ExperimentMetricOptions; value: MetricToWidgetId }
  secondaryMetrics: { label: ExperimentMetricOptions; value: MetricToWidgetId }[]
  variants: ExperimentAnalyticsVariantType[]
}

export type ExperimentAnalyticsVariantType = {
  id: string
  name: string
  description: string
  trafficPercentage: number
  isControlGroup: boolean
}
